import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import ProtectedRoute from '../../components/ProtectedRoute';

const getDateValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getUserFactionIds = (user) => {
  const ids = [
    user?.favoriteFactionId,
    user?.factionId,
    ...(Array.isArray(user?.factionIds) ? user.factionIds : []),
    user?.roadToFactionId,
  ].filter(Boolean);

  return [...new Set(ids)];
};

const getUserFactionCount = (user) => getUserFactionIds(user).length;

const getUserPrimaryFactionId = (user) => getUserFactionIds(user)[0] || null;

const getUserCreatedAt = (user, createdAtMap = {}) => (
  getDateValue(user?.createdAt) || createdAtMap[user?.id] || null
);

const hasActiveNotifications = (user) => (
  user?.notificationsEnabled === true &&
  typeof user?.fcmToken === 'string' &&
  user.fcmToken.trim().length > 0
);

export default function AddUser() {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [factionNames, setFactionNames] = useState({});
  const [userCreatedAtMap, setUserCreatedAtMap] = useState({});
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('username');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  const dashboardPreset = useMemo(
    () => new URLSearchParams(location.search).get('preset') || '',
    [location.search]
  );

  const dashboardPresetMeta = useMemo(() => {
    switch (dashboardPreset) {
      case 'recent-7':
        return { label: 'Nuovi utenti negli ultimi 7 giorni' };
      case 'recent-30':
        return { label: 'Nuovi utenti negli ultimi 30 giorni' };
      case 'factions-0':
        return { label: 'Utenti senza fazioni' };
      case 'factions-1':
        return { label: 'Utenti con 1 fazione' };
      case 'factions-2':
        return { label: 'Utenti con 2 fazioni' };
      case 'factions-3plus':
        return { label: 'Utenti con 3 o più fazioni' };
      case 'notifications-active':
        return { label: 'Utenti con notifiche attive' };
      default:
        return null;
    }
  }, [dashboardPreset]);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const [snap, usernamesSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'usernames')),
      ]);
      const userList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(userList);

      const createdAtByUid = {};
      usernamesSnap.forEach((docSnap) => {
        const data = docSnap.data();
        if (data?.uid) {
          createdAtByUid[data.uid] = getDateValue(data.createdAt);
        }
      });
      setUserCreatedAtMap(createdAtByUid);

      const uniqueFactionIds = [...new Set(userList.flatMap(user => getUserFactionIds(user)))];
      const names = {};
      for (const fid of uniqueFactionIds) {
        try {
          const fSnap = await getDoc(doc(db, 'factions', fid));
          names[fid] = fSnap.exists() ? (fSnap.data().name || '-') : '-';
        } catch {
          names[fid] = '-';
        }
      }
      setFactionNames(names);
    } catch (err) {
      setMessage('Errore caricamento utenti');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Eliminare questo utente?')) return;
    try {
      await deleteDoc(doc(db, 'users', id));
      setMessage('Utente eliminato');
      loadUsers();
    } catch (err) {
      setMessage('Errore eliminazione');
    }
  }

  const adminCount = users.filter(u => u.role === 'admin').length;
  const noFactionCount = users.filter(u => getUserFactionCount(u) === 0).length;

  const filteredUsersRaw = users.filter(u => {
    const factionCount = getUserFactionCount(u);

    if (filter === 'nofaction') return factionCount === 0;
    if (filter === 'admin') return u.role === 'admin';
    if (filter === 'user') return u.role === 'user';
    return true;
  });

  function handleSort(field) {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  }

  const filteredUsers = useMemo(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);

    const presetFiltered = filteredUsersRaw.filter(u => {
      const factionCount = getUserFactionCount(u);
      const createdAt = getDateValue(u.createdAt) || userCreatedAtMap[u.id] || null;

      switch (dashboardPreset) {
        case 'recent-7':
          return Boolean(createdAt && createdAt >= sevenDaysAgo);
        case 'recent-30':
          return Boolean(createdAt && createdAt >= thirtyDaysAgo);
        case 'factions-0':
          return factionCount === 0;
        case 'factions-1':
          return factionCount === 1;
        case 'factions-2':
          return factionCount === 2;
        case 'factions-3plus':
          return factionCount >= 3;
        case 'notifications-active':
          return hasActiveNotifications(u);
        default:
          return true;
      }
    });

    const searched = presetFiltered.filter(u => {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return true;
      const factionLabels = getUserFactionIds(u).map((factionId) => factionNames[factionId] || factionId);
      return [u.username, u.email, ...factionLabels]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(term));
    });

    return [...searched].sort((a, b) => {
      let aValue;
      let bValue;
      if (sortBy === 'faction') {
        const aFactionId = getUserPrimaryFactionId(a);
        const bFactionId = getUserPrimaryFactionId(b);
        aValue = aFactionId ? (factionNames[aFactionId] || '') : '';
        bValue = bFactionId ? (factionNames[bFactionId] || '') : '';
      } else if (sortBy === 'createdAt') {
        aValue = getUserCreatedAt(a, userCreatedAtMap)?.getTime() || 0;
        bValue = getUserCreatedAt(b, userCreatedAtMap)?.getTime() || 0;
      } else {
        aValue = (a[sortBy] || '').toLowerCase();
        bValue = (b[sortBy] || '').toLowerCase();
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [dashboardPreset, factionNames, filteredUsersRaw, searchTerm, sortBy, sortDirection, userCreatedAtMap]);

  function handleCopyEmails() {
    const emails = filteredUsers.map(u => u.email).filter(Boolean).join(', ');
    if (emails) {
      navigator.clipboard.writeText(emails);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setMessage('Nessuna email da copiare.');
    }
  }

  return (
    <ProtectedRoute>
      <div className="container admin-page py-4">

        {message && (
          <div className={`alert ${message.toLowerCase().includes('errore') ? 'alert-danger' : 'alert-success'} py-2 mb-4`}>
            {message}
          </div>
        )}

        <div className="admin-stats-grid mb-4">
          <div className="admin-stat-card">
            <span>Utenti Totali</span>
            <strong>{users.length}</strong>
          </div>
          <div className="admin-stat-card">
            <span>Admin</span>
            <strong>{adminCount}</strong>
          </div>
          <div className="admin-stat-card">
            <span>Senza Fazione</span>
            <strong>{noFactionCount}</strong>
          </div>
        </div>

        <div className="card shadow-sm admin-list-card">
          <div className="card-header bg-light py-3">
            <div className="d-flex flex-column gap-3">
              {dashboardPresetMeta && (
                <div className="alert alert-info d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 mb-0">
                  <div>
                    <strong className="d-block">Filtro dashboard attivo</strong>
                    <span>{dashboardPresetMeta.label}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => navigate('/admin/users')}
                  >
                    Mostra tutti
                  </button>
                </div>
              )}

              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                <h5 className="mb-0">
                  <i className="bi bi-people-fill me-2"></i>
                  Lista Utenti ({filteredUsers.length})
                </h5>
                <div className="input-group" style={{ maxWidth: '360px' }}>
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Cerca username, email o fazione..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
                <div className="d-flex flex-wrap gap-2">
                  <button className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilter('all')}>Tutti</button>
                  <button className={`btn btn-sm ${filter === 'nofaction' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilter('nofaction')}>Senza Fazione</button>
                  <button className={`btn btn-sm ${filter === 'admin' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilter('admin')}>Admin</button>
                  <button className={`btn btn-sm ${filter === 'user' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilter('user')}>User</button>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <button className="btn btn-outline-info btn-sm" type="button" onClick={handleCopyEmails}>
                    <i className="bi bi-clipboard me-1"></i>
                    Copia email filtrate
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" type="button" onClick={loadUsers}>
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Ricarica
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card-body">
            {loading ? (
              <div className="p-4 text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Caricamento...</span>
                </div>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-dark mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('username')}>
                          <div className="d-flex align-items-center">
                            Username
                            {sortBy === 'username' && (
                              <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-1`}></i>
                            )}
                          </div>
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>
                          <div className="d-flex align-items-center">
                            Email
                            {sortBy === 'email' && (
                              <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-1`}></i>
                            )}
                          </div>
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSort('faction')}>
                          <div className="d-flex align-items-center">
                            Fazione
                            {sortBy === 'faction' && (
                              <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-1`}></i>
                            )}
                          </div>
                        </th>
                        <th style={{ cursor: 'pointer', minWidth: '170px' }} onClick={() => handleSort('createdAt')}>
                          <div className="d-flex align-items-center">
                            Data iscrizione
                            {sortBy === 'createdAt' && (
                              <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-1`}></i>
                            )}
                          </div>
                        </th>
                        <th style={{ width: '70px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map(u => {
                        const primaryFactionId = getUserPrimaryFactionId(u);
                        const factionCount = getUserFactionCount(u);
                        const createdAt = getUserCreatedAt(u, userCreatedAtMap);

                        return (
                        <tr key={u.id}>
                          <td>
                            <div className="fw-semibold">{u.username || '-'}</div>
                            {u.role && (
                              <small className="text-muted">{u.role}</small>
                            )}
                          </td>
                          <td>{u.email || '-'}</td>
                          <td>
                            <div>{primaryFactionId ? (factionNames[primaryFactionId] || primaryFactionId) : '-'}</div>
                            {factionCount > 1 && (
                              <small className="text-muted">{factionCount} fazioni</small>
                            )}
                          </td>
                          <td>
                            {createdAt ? createdAt.toLocaleString('it-IT') : '-'}
                          </td>
                          <td>
                            <div className="d-flex gap-1 justify-content-end">
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(u.id)} title="Elimina">
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>

                {filteredUsers.length === 0 && (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-emoji-frown fs-1"></i>
                    <p className="mt-3 mb-0">Nessun utente trovato</p>
                  </div>
                )}

                {copied && (
                  <div className="alert alert-success m-3 py-2 text-center">
                    <i className="bi bi-clipboard-check me-2"></i>
                    Email copiate negli appunti!
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}
